import { motion } from "framer-motion";
import { Reveal } from "@/components/motion/Reveal";
import { REASONS, STATS, IMAGES } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

function Chapter({ item, index }) {
  const Icon = item.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.7, ease: EASE, delay: (index % 3) * 0.08 }}
      className="group relative rounded-3xl border border-[#EEF1F4] bg-white p-8 lg:p-10 overflow-hidden hover:border-[#d1d5db] transition-colors duration-500"
      data-testid={`reason-${item.n}`}
    >
      <span className="pointer-events-none absolute -top-6 right-2 font-display text-[7rem] leading-none font-extrabold text-[#EEF1F4] select-none">
        {item.n}
      </span>
      <span className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-[#374151] text-white">
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="relative mt-6 font-display text-xl lg:text-2xl font-bold tracking-tight text-[#1F2937]">
        {item.title}
      </h3>
      <p className="relative mt-3 text-sm lg:text-base leading-relaxed text-[#6B7280]">
        {item.desc}
      </p>
    </motion.div>
  );
}

export default function WhyChooseUs() {
  return (
    <section
      id="why"
      className="relative py-24 lg:py-36 bg-[#F5F6F8]"
      data-testid="why-section"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">
                Why choose us
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 max-w-3xl font-display text-4xl lg:text-5xl font-extrabold tracking-tighter leading-[1.02] text-[#1F2937]">
                Built for enterprises that refuse to compromise
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <div className="relative overflow-hidden rounded-3xl border border-[#EEF1F4]">
                <img
                  src={IMAGES.trust}
                  alt="Enterprise partnership"
                  className="h-40 w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F2937]/50 to-transparent" />
                <p className="absolute bottom-4 left-5 text-sm font-semibold text-white">
                  Trusted by global enterprises
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {REASONS.map((r, i) => (
            <Chapter key={r.n} item={r} index={i} />
          ))}
        </div>

        {/* Stats band */}
        <Reveal delay={0.1} className="mt-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px rounded-3xl overflow-hidden border border-[#EEF1F4] bg-[#EEF1F4]">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="bg-white p-8 lg:p-10"
                data-testid={`stat-${s.label.split(" ")[0].toLowerCase()}`}
              >
                <p className="font-display text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#1F2937]">
                  {s.value}
                </p>
                <p className="mt-2 text-sm text-[#6B7280]">{s.label}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
